<template>
    <div id="app"
    class="app-container"
    :class="{
        'no-header': (!globals.publicConfig
        || !globals.publicConfig.website
        || !globals.publicConfig.website.showHeader)
    }">
        <router-view v-if="initReady"></router-view>
        <MyBetsModal v-if="mybetsModalOpen" />
        <template v-if="ticketModal">
            <TicketModalMobile v-if="isMobile" v-on:toggleTicketModal="closeTicketModal" />
            <TicketModalDesktop v-else v-on:toggleTicketModal="closeTicketModal" />
        </template>
        <div v-if="fatalError">
            No internet connection, please reload page
        </div>

        <label for="maintenance-notification" v-if="globals.publicConfig && globals.publicConfig.maintenance && globals.publicConfig.maintenance.messageActive" class="maintenance">
            <input id="maintenance-notification" type="checkbox">
            <p class="show" @click="showMaintenanceMessage = true">{{ $t('SHOW') }}</p>
            <div class="notification-bar" v-if="showMaintenanceMessage">
                <div class="container">
                    <p>{{ globals.publicConfig.maintenance.message }}</p>
                    <p class="close" @click="showMaintenanceMessage = false">{{ $t('CLOSE') }}</p>
                </div>
            </div>
        </label>
    </div>
</template>

<script>
import
{
    mapGetters,
    mapActions,
    mapMutations,
}
from 'vuex';
import { mapActions as piniaActions } from 'pinia';
import VueGtm from '@gtm-support/vue2-gtm';
import Vue from 'vue';
import TicketModalDesktop from './components/TicketModalDesktop.vue';
import TicketModalMobile from './components/TicketModalMobile.vue';
import MyBetsModal from './views/MyBets.vue';
import { useOfferStore } from './store/piniaStore/offer';
import { useBetslipStore } from './store/piniaStore/betslip';
import { useCustomizationStore } from './store/piniaStore/customization';

export default
{
    name: 'app',
    data()
    {
        const me = this;

        return {
            alertshown: false,
            checkedTrans: [],
            ticketModal: false,
            urlLangCode: false,
            resizeInterval: null,
            bodyHeight: (me.isMobile) ? 300 : 800,
            showMaintenanceMessage: true,
            getBalanceIntervalAllowed: false,
            getBalanceInterval: null,
        };
    },
    components:
    {
        TicketModalDesktop,
        TicketModalMobile,
        MyBetsModal,
    },
    destroyed()
    {
        const me = this;

        clearInterval(me.resizeInterval);
        clearInterval(me.getBalanceInterval);
    },
    async created()
    {
        const me = this;
        me.offerStore = useOfferStore();
        me.betslipStore = useBetslipStore();
        try
        {
            await me.init({
                storage: window.localStorage,
                ticketConfigType: 'web',
            });

            await window.emit('preload', {
                route: me.$route,
            });

            window.subscribeToTopic('setGetBalanceInterval', this.setGetBalanceInterval);

            try
            {
                await me.authenticateToken();
            }
            catch (e)
            {
                console.error(e);
            }

            try
            {
                if (me.$route
                && me.$route.params
                && me.$route.params.lang
                && me.$route.params.lang !== '')
                {
                    me.urlLangCode = true;
                    await me.getTranslationByLangCode({
                        langCode: me.$route.params.lang,
                        reloadGlobals: false,
                    });
                    await me.$nextTick();
                }
            }
            catch (e)
            {
                console.error(e);
            }

            await me.getGlobals();

            if (this.getBalanceIntervalAllowed
            && this.globals
            && this.globals.publicConfig
            && this.globals.publicConfig.website
            && this.globals.publicConfig.website.initiateGetBalanceInterval)
            {
                this.getBalanceInterval = setInterval(() =>
                {
                    this.getBalance();
                }, 5000);
            }

            if (me.$route.query.freePlay && me.$route.query.freePlay === 'true')
            {
                me.setFreePlay(true);
            }

            me.setCurrency(me.globals.publicConfig.currency);

            me.setMaintenance();

            me.setBetRadarStats();

            me.initGoogleTagManager();

            me.offerStore.initOffer();
            await me.initBetslip();
        }
        catch (error)
        {
            console.error(error);
            return;
        }

        try
        {
            me.initOnlineTicketPlacementListeners();
            me.initIframeResizeListeners();
            me.initFavourites();
        }
        catch (e)
        {
            console.error(e);
        }

        if (me.$route
            && me.$route.query
            && me.$route.query.ticketCode
            && me.$route.query.ticketCode !== '')
        {
            me.$nextTick(() =>
            {
                try
                {
                    let { ticketCode } = me.$route.query;
                    if (ticketCode.includes(' '))
                    {
                        ticketCode = ticketCode.split(' ').join('');
                    }

                    me.getTicketData({
                        ticketCode,
                    });
                }
                catch (e)
                {
                    console.error(e);
                }
            });
        }

        me.initNotifications();

        window.emit('afterload', me.$route);
    },
    methods:
    {
        ...mapActions([
            'fetchLanguage',
            'setTicketData',
            'checkTrans',
            'getTranslationByLangCode',
            'setTree',
            'init',
            'getGlobals',
            'toggleSportTicketUpdates',
            'getTicketData',
            'changeCanPlaceOnlineTicket',
            'setMaintenance',
            'saveItemsToStorage',
            'getBalance',
        ]),
        ...mapMutations([
            'setCurrency',
            'setFreePlay',
        ]),
        ...mapActions('favourites', [
            'initFavourites',
        ]),
        ...piniaActions(useCustomizationStore, ['setCustomCss']),
        setGetBalanceInterval(val)
        {
            this.getBalanceIntervalAllowed = val;
        },
        initNotifications()
        {
            const me = this;
            const events = new EventSource('/session/getnotifications');

            events.addEventListener('configchange', async () =>
            {
                await me.getGlobals();

                me.setMaintenance();
            });

            events.onerror = () =>
            {
                events.close();
                setTimeout(() =>
                {
                    me.initNotifications();
                }, Math.random() * 10000);
            };
        },
        async initBetslip()
        {
            if (this.hasBetslip)
            {
                await this.betslipStore.initBetslip(
                    this.globals.ticketConfig,
                    this.playerdata,
                );

                await this.betslipStore.wipeAllBetslipData({
                    skipSportWipe: true,
                });
            }
        },
        initGoogleTagManager()
        {
            const me = this;
            if (me.globals.publicConfig
            && me.globals.publicConfig.web
            && me.globals.publicConfig.website.googleTagManager)
            {
                const gtmConfig = me.globals.publicConfig.website.googleTagManager;
                Vue.use(VueGtm, {
                    id: gtmConfig.id,
                    defer: true,
                    debug: true, // Whether or not display console logs debugs (optional)
                    vueRouter: me.$router, // Pass the router instance to automatically sync with router (optional)
                    // trackOnNextTick: false // Whether or not call trackView in Vue.nextTick
                });
            }
        },
        resizeIframe()
        {
            const me = this;

            if (me.bodyHeight !== document.body.scrollHeight)
            {
                window.parent.postMessage({ height: document.body.scrollHeight }, '*');
                me.bodyHeight = document.body.scrollHeight;
            }
        },
        closeTicketModal()
        {
            const me = this;
            me.ticketModal = false;
            me.setTicketData(null);
        },
        setBetRadarStats()
        {
            const me = this;

            if (me.globals.publicConfig.statistics && me.globals.publicConfig.statistics.betRadar)
            {
                try
                {
                // eslint-disable-next-line func-names
                    (function (a, b, c, d, e, f, g, h, i)
                    {
                        // eslint-disable-next-line
                        (i = a[e] = function ()
                        {
                            // eslint-disable-next-line prefer-rest-params
                            (a[e].q = a[e].q || []).push(arguments);
                        }, i.l = 1 * new Date(), i.o = f,
                        // eslint-disable-next-line
                        g = b.createElement(c), h = b.getElementsByTagName(c)[0], g.async = 1, g.src = d, g.setAttribute('n', e), h.parentNode.insertBefore(g, h)
                        );
                    }(window, document, 'script', me.globals.publicConfig.statistics.betRadar.scriptUrl, 'SIR', {
                        theme: false, // using custom theme
                        language: (me.globals && me.globals.langCode) ? me.globals.langCode.split('-')[0] : 'en',
                    }));
                }
                catch (error)
                {
                    console.error('br stats', error);
                }
            }

            /* if (me.globals.publicConfig
            && me.globals.publicConfig.website
            && me.globals.publicConfig.website.sportStreaming != null
            && me.globals.publicConfig.website.sportStreaming.scriptUrl)
            {
                const streamingScript = document.createElement('script');
                streamingScript.setAttribute('src', me.globals.publicConfig.website.sportStreaming.scriptUrl);
                document.head.appendChild(streamingScript);
            } */
        },
        //
        async authenticateToken()
        {
            const me = this;

            await window.emit('loaded', {
                token: me.$route.query.token,
                query: me.$route.query,
            });

            try
            {
                await window.authenticateToken({
                    token: me.$route.query.token,
                    query: me.$route.query,
                });
            }
            catch (error)
            {
                // fall silently
            }
        },
        async initIframeResizeListeners()
        {
            const me = this;

            window.subscribeToTopic('iframeResizeInitResponse', () =>
            {
                me.resizeIframe();

                // resize event handler
                window.subscribeToTopic('resizeEventTriggered', me.resizeIframe);

                me.resizeInterval = setInterval(() =>
                {
                    me.resizeIframe();
                }, 1000);
            });

            window.emit('iframeResizeInit');
        },
        initOnlineTicketPlacementListeners()
        {
            const me = this;

            window.subscribeToTopic('canPlaceOnlineTicketChanged', (val) =>
            {
                if (val != null)
                {
                    me.changeCanPlaceOnlineTicket(val);
                }
            });

            window.emit('activateCanPlaceOnlineTicketListener', {
                query: me.$route.query,
            });
        },
        setCommonLangCode(val = null)
        {
            const me = this;
            let langCode = null;

            if (val != null)
            {
                langCode = val;
            }
            else if (me.globals.langCode != null)
            {
                langCode = me.globals.langCode;
            }
            else
            {
                return;
            }

            me.$setCommonLangCode(langCode);
        },
    },
    computed:
    {
        ...mapGetters([
            'globals',
            'translation',
            'langInitialized',
            'isMobile',
            'betslip',
            'ticketData',
            'betslipType',
            'loggedIn',
            'ticketLoaded',
            'mybetsModalOpen',
            'fatalError',
            'globalsInitialized',
            'betslipItems',
            'playerdata',
        ]),
        hasBetslip()
        {
            if (this.$route
            && this.$route.meta
            && this.$route.meta.hasBetslip != null)
            {
                return this.$route.meta.hasBetslip;
            }

            return true;
        },
        initReady()
        {
            const me = this;

            return me.langInitialized && me.globalsInitialized
                && this.offerStore.offerInitialized && this.offerStore.getOfferInstance() != null;
        },
    },
    watch:
    {
        betslipItems:
        {
            handler(val)
            {
                if (val.length)
                {
                    this.saveItemsToStorage();
                }
                /* const me = this;
                if (val != null
                && val !== undefined
                && me.betslip != null
                && !me.betslip.blockRecalculation)
                {
                    window.localStorage.setItem(
                        me.betslipType,
                        JSON.stringify(val),
                    );
                } */
            },
            deep: true,
        },
        ticketData(val)
        {
            const me = this;
            if (val != null)
            {
                me.ticketModal = true;
            }
        },
        'globals.langId': {
            handler: async function gId(value, oldValue)
            {
                const me = this;

                if (oldValue === undefined
                && !me.urlLangCode)
                {
                    await me.fetchLanguage(value);
                }
            },
        },
        'globals.langCode'(val)
        {
            const me = this;

            me.setCommonLangCode(val);

            me.setBetRadarStats();
        },
        'globals.groupId'()
        {
            window.emit('groupIdChanged');
        },
        translation(value)
        {
            const me = this;

            this.$i18n.setLocaleMessage(`ln${value.key}`, value.root);
            this.$i18n.locale = `ln${value.key}`;

            if (me.$route.meta && me.$route.meta.title && me.$route.meta.title !== '')
            {
                document.title = me.$t(`PAGETITLE_${me.$route.meta.title}`);
            }
        },
        langInitialized(value)
        {
            const me = this;
            if (value)
            {
                this.$i18n.missing = (handler, key) =>
                {
                    if (me.checkedTrans.indexOf(key) !== -1)
                    {
                        return;
                    }
                    me.checkedTrans.push(key);
                    me.checkTrans(key);
                };
            }
        },
        '$route.params.lang'(val)
        {
            const me = this;
            if (val !== undefined && val !== '' && me.globals.langCode !== val)
            {
                me.getTranslationByLangCode({
                    langCode: val,
                    reloadGlobals: true,
                });
            }
        },
        '$route.query'(val)
        {
            window.emit('activateCanPlaceOnlineTicketListener', {
                query: val,
            });
        },
        betslipType(val)
        {
            if (val === 'sport')
            {
                // TODO check this for betslip integration
                // me.toggleSportTicketUpdates();
            }
        },
        'globals.customCss'(val)
        {
            if (!val || !val.web)
            {
                return;
            }
            this.setCustomCss(val.web);
        },
    },
};
</script>
